import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, CreditCard, Settings, 
  LogOut, Menu, X, Shield, ChevronDown, ChevronRight, User, Wallet,
  Wrench, Bell, Mail, Palette, Gift, Globe, FilePlus, List,
  ShieldCheck, ScrollText, KeyRound
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // SECURITY: Verify admin auth on mount and route change
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    if (!adminToken || !adminUser) {
      navigate('/gmbadmin', { replace: true });
      return;
    }
    setAuthChecked(true);
  }, [navigate, location.pathname]);

  // Auto-expand submenus when on their pages
  useEffect(() => {
    if (location.pathname.startsWith('/gmbadmin/settings')) {
      setSettingsOpen(true);
    }
    if (location.pathname.startsWith('/gmbadmin/referrals')) {
      setReferralOpen(true);
    }
    if (location.pathname.startsWith('/gmbadmin/pages') || location.pathname.startsWith('/gmbadmin/faqs')) {
      setPagesOpen(true);
    }
    if (location.pathname.startsWith('/gmbadmin/security')) {
      setSecurityOpen(true);
    }
  }, [location.pathname]);

  const mainMenuItems = [
    { path: '/gmbadmin/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard' },
    { path: '/gmbadmin/users', icon: <Users className="w-5 h-5" />, label: 'Users' },
    { path: '/gmbadmin/posts', icon: <FileText className="w-5 h-5" />, label: 'Posts' },
    { path: '/gmbadmin/transactions', icon: <CreditCard className="w-5 h-5" />, label: 'Transactions' },
    { path: '/gmbadmin/membership', icon: <Settings className="w-5 h-5" />, label: 'Membership Plans' },
    { path: '/gmbadmin/templates', icon: <Palette className="w-5 h-5" />, label: 'Creative Templates' },
  ];

  const settingsSubItems = [
    { path: '/gmbadmin/settings/site', icon: <Globe className="w-4 h-4" />, label: 'Site Settings' },
    { path: '/gmbadmin/settings/payment-gateway', icon: <Wallet className="w-4 h-4" />, label: 'Payment Gateway' },
    { path: '/gmbadmin/settings/email-notifications', icon: <Mail className="w-4 h-4" />, label: 'Email Notifications' },
    { path: '/gmbadmin/settings/maintenance', icon: <Wrench className="w-4 h-4" />, label: 'Maintenance Mode' },
    { path: '/gmbadmin/settings/alert', icon: <Bell className="w-4 h-4" />, label: 'Alert Banner' },
    { path: '/gmbadmin/settings/smtp', icon: <Mail className="w-4 h-4" />, label: 'SMTP Configuration' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/gmbadmin');
  };

  const isSettingsActive = location.pathname.startsWith('/gmbadmin/settings');
  const isReferralActive = location.pathname.startsWith('/gmbadmin/referrals');
  const isPagesActive = location.pathname.startsWith('/gmbadmin/pages') || location.pathname.startsWith('/gmbadmin/faqs');
  const isSecurityActive = location.pathname.startsWith('/gmbadmin/security');

  // Don't render until auth is verified
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const referralSubItems = [
    { path: '/gmbadmin/referrals/settings', icon: <Settings className="w-4 h-4" />, label: 'Settings' },
    { path: '/gmbadmin/referrals/list', icon: <Users className="w-4 h-4" />, label: 'Referrals' },
    { path: '/gmbadmin/referrals/payouts', icon: <Wallet className="w-4 h-4" />, label: 'Payouts' },
  ];

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-56 bg-[#202124] text-white flex flex-col transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-base" style={{ fontFamily: 'Outfit' }}>GMB Admin</span>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {mainMenuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#1A73E8] text-white'
                  : 'text-[#9AA0A6] hover:bg-white/10 hover:text-white'
              }`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}


          {/* Pages Parent */}
          <div>
            <button
              onClick={() => setPagesOpen(!pagesOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isPagesActive ? 'bg-white/10 text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'
              }`}
              data-testid="admin-pages-menu"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5" />
                <span>Pages</span>
              </div>
              {pagesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {pagesOpen && (
              <div className="ml-4 mt-1 space-y-1">
                <Link to="/gmbadmin/pages" className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/gmbadmin/pages' ? 'bg-[#1A73E8] text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'}`} data-testid="admin-manage-pages-menu">
                  <List className="w-4 h-4" /><span>Manage Pages</span>
                </Link>
                <Link to="/gmbadmin/faqs" className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/gmbadmin/faqs' ? 'bg-[#1A73E8] text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'}`} data-testid="admin-faqs-menu">
                  <FileText className="w-4 h-4" /><span>FAQs</span>
                </Link>
              </div>
            )}
          </div>


          {/* Security Parent */}
          <div>
            <button
              onClick={() => setSecurityOpen(!securityOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isSecurityActive ? 'bg-white/10 text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'
              }`}
              data-testid="admin-security-menu"
            >
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5" />
                <span>Security</span>
              </div>
              {securityOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {securityOpen && (
              <div className="ml-4 mt-1 space-y-1">
                <Link to="/gmbadmin/security/2fa-users" className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/gmbadmin/security/2fa-users' ? 'bg-[#1A73E8] text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'}`} data-testid="admin-2fa-users-menu">
                  <KeyRound className="w-4 h-4" /><span>2FA Management</span>
                </Link>
                <Link to="/gmbadmin/security/audit-log" className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/gmbadmin/security/audit-log' ? 'bg-[#1A73E8] text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'}`} data-testid="admin-audit-log-menu">
                  <ScrollText className="w-4 h-4" /><span>Audit Log</span>
                </Link>
                <Link to="/gmbadmin/security/api-keys" className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === '/gmbadmin/security/api-keys' ? 'bg-[#1A73E8] text-white' : 'text-[#9AA0A6] hover:bg-white/5 hover:text-white'}`} data-testid="admin-api-keys-menu">
                  <KeyRound className="w-4 h-4" /><span>API Keys</span>
                </Link>
              </div>
            )}
          </div>


          {/* Referral Program Parent */}
          <div>
            <button
              onClick={() => setReferralOpen(!referralOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isReferralActive
                  ? 'bg-white/10 text-white'
                  : 'text-[#9AA0A6] hover:bg-white/10 hover:text-white'
              }`}
              data-testid="admin-nav-referrals"
            >
              <span className="flex items-center gap-3">
                <Gift className="w-5 h-5" />
                Referral Program
              </span>
              {referralOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {referralOpen && (
              <div className="ml-4 mt-1 space-y-0.5">
                {referralSubItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      location.pathname === item.path
                        ? 'bg-[#1A73E8] text-white'
                        : 'text-[#9AA0A6] hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Settings Parent */}
          <div>
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isSettingsActive
                  ? 'bg-white/10 text-white'
                  : 'text-[#9AA0A6] hover:bg-white/10 hover:text-white'
              }`}
              data-testid="settings-parent-menu"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                Settings
              </div>
              {settingsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>

            {settingsOpen && (
              <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                {settingsSubItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      location.pathname === item.path
                        ? 'bg-[#1A73E8] text-white'
                        : 'text-[#9AA0A6] hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Profile */}
          <Link
            to="/gmbadmin/profile"
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              location.pathname === '/gmbadmin/profile'
                ? 'bg-[#1A73E8] text-white'
                : 'text-[#9AA0A6] hover:bg-white/10 hover:text-white'
            }`}
          >
            <User className="w-5 h-5" />
            Profile
          </Link>
        </nav>

        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#9AA0A6] hover:bg-white/10 hover:text-white transition-colors w-full"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-14 bg-white border-b border-[#DADCE0] flex items-center justify-between px-6 sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-[#5F6368]">
            <Menu className="w-5 h-5" />
          </button>
          <div />
          <Link to="/" className="text-sm text-[#1A73E8] hover:underline" data-testid="view-user-site">
            View User Site &rarr;
          </Link>
        </header>
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
